import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  Inject,
} from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Validators, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import {
  AssignmentCustomerResponse,
  baseAssignmentsActions,
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  BaseModalService,
  BaseOfferStatus,
  BaseTabInterface,
  RequirementCategoryResponse,
  BaseDeregisterFormInterface,
  BaseDateService,
  BaseContractSigningTypeEnum,
  ContractResponse,
  BaseSigningStatusType,
  BaseRolesService,
  BASE_PERMISSIONS,
  BaseSignContractOptions,
  BaseSignatureService,
  baseSharedActions,
  baseInquiryActions,
  ENVIRONMENTER,
  CustomerInquiryOfferListItemWithOrder,
  BaseInquiryType,
  BaseSigningStatus,
} from 'base';
import { BaseCustomerOfferActions } from '../../_enums/base-customer-offer-actions.enum';
import {
  BaseCustomerOfferInitialTabList,
  BaseCustomerOfferInitialAdminTabList,
} from '../../_const/base-customer-offer-tab-list.const';
import { BaseCustomerOfferTabsEnum } from '../../_enums/base-customer-offer-tabs.enum';
import { BaseCustomerOfferActionsService } from '../../../../shared/_services/base-customer-offer-actions.service';
import { ExternalEmployeeService } from '../../../external-employee/external-employee.service';

@Component({
  selector: 'base-customer-offer-info-modal',
  templateUrl: './base-customer-offer-info-modal.component.html',
})
export class BaseCustomerOfferInfoModalComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('deregisterCandidateModalBody') deregisterCandidateModalBody: ElementRef;
  @ViewChild('addSignSelectionPopup') addSignSelectionPopup: ElementRef;
  @ViewChild('addSignPopup') addSignPopup: ElementRef;
  @ViewChild('inquireEmployeeModal') inquireEmployeeModal: ElementRef;
  @Input() offer: CustomerInquiryOfferListItemWithOrder | undefined;
  @Input() inquiry: BaseInquiryType;
  @Input() inquiryRequirementCategories: RequirementCategoryResponse[] = [];
  @Input() assignment: AssignmentCustomerResponse | undefined;
  @Input() assignmentContractSigningStatus: BaseSigningStatusType = BaseSigningStatus.unsigned;
  @Input() openOperationInfo: boolean;
  @Input() isAssignmentsAdmin: boolean;
  @Input() modalRef: NgbModalRef;
  @Output() acceptOrRejectOffer: EventEmitter<BaseCustomerOfferActions> = new EventEmitter<BaseCustomerOfferActions>();
  tabs: BaseTabInterface[] = [];
  currentTab: BaseTabInterface;
  isOfferConfirmed: boolean;
  isOfferOpen: boolean;
  isFullySigned: boolean;
  isSignedByPartner: boolean;
  isSignedByMyself: boolean;
  isNotSigned: boolean;
  isAssignmentDeRegistered = false;
  isEndedAssignment = false;
  deregisterFormSubmitted = false;
  statusButtonBorder: string;
  avatarSize = BaseAvatarSize;
  buttonSizes = BaseButtonSize;
  customerOfferActions = BaseCustomerOfferActions;
  offerStatuses = BaseOfferStatus;
  signingStatusBySide = BaseSigningStatus;
  contractSigningType = BaseContractSigningTypeEnum;
  deregisterForm: FormGroup<BaseDeregisterFormInterface>;
  validContract: ContractResponse;
  customerOfferTabsEnum = BaseCustomerOfferTabsEnum;
  isAcceptButtonAvailable = false;
  isRejectButtonAvailable = false;
  hasSignContractPermission = false;
  addSignModalRef: NgbModalRef;
  digitalSignModalRef: NgbModalRef;
  signingType: BaseSignContractOptions | undefined;
  inquireEmployeeModalModalRef: NgbModalRef;

  constructor(
    private fb: NonNullableFormBuilder,
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private baseRolesService: BaseRolesService,
    private offerActionsService: BaseCustomerOfferActionsService,
    private externalEmployeeService: ExternalEmployeeService,
    private baseSignatureService: BaseSignatureService,
    @Inject(ENVIRONMENTER) private environment: any
  ) {}

  get showWorkCouncilTab() {
    return (
      this.baseRolesService.hasWorkCouncilRole() ||
      this.baseRolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_WORK_COUNCIL_COMMUNICATION)
    );
  }

  ngOnInit(): void {
    this.hasSignContractPermission = this.baseRolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_SIGN_CONTRACT
    );
    this.isOfferConfirmed = this.offer?.status === this.offerStatuses.confirmed;
    this.isOfferOpen = this.offer?.status === this.offerStatuses.open;
    this.setTabs();

    this.setAssignmentEnded();
    this.setAssignmentSignStatus();
    this.statusButtonBorder = this.setSignButtonClass();

    const locationWorkCouncilSettings = {
      workCouncilActivated: !!this.inquiry.customerLocation?.workCouncilActivated,
      acceptOfferAfterPreselection: !!this.inquiry.customerLocation?.acceptOfferAfterPreselection,
      acceptOfferAfterWorkCouncilApproval: !!this.inquiry.customerLocation?.acceptOfferAfterWorkCouncilApproval,
    };
    this.isAcceptButtonAvailable = this.offerActionsService.checkAcceptButtonAvailability(
      this.offer,
      locationWorkCouncilSettings
    );
    this.isRejectButtonAvailable = this.offerActionsService.checkRejectButtonAvailability(this.offer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.assignment) {
      this.validContract = this.assignment?.contracts?.find((contract) => contract.valid) as ContractResponse;
      this.setAssignmentEnded();
      this.setAssignmentSignStatus();
      this.statusButtonBorder = this.setSignButtonClass();
    }
  }

  ngOnDestroy() {
    this.store.dispatch(baseAssignmentsActions.resetAssignment());
    this.acceptOrRejectOffer.emit(this.customerOfferActions.default);
    this.store.dispatch(baseAssignmentsActions.clearWorkCouncilCommentList());
  }

  onTabChange(event: BaseTabInterface): void {
    this.currentTab = event;
  }

  closeModal(): void {
    this.modalRef.close();
  }

  onAcceptOrRejectOffer(action: BaseCustomerOfferActions): void {
    this.acceptOrRejectOffer.emit(action);
    this.closeModal();
  }

  openCandidateDeregisterModal(): void {
    if (!this.deregisterForm) {
      this.deregisterForm = this.fb.group({
        cancelationDate: ['', [Validators.required]],
        note: [''],
      });
    }

    const deregisterModalRef = this.modalService.open(
      {
        headText: 'externalEmployees.unsubscribe',
        bodyTemplate: this.deregisterCandidateModalBody,
        okText: 'action.yesSave',
        cancelText: 'action.cancel',
        canBeClosed: () => {
          this.deregisterFormSubmitted = true;
          return this.deregisterForm.valid;
        },
      },
      {
        size: 'lg',
      }
    );

    deregisterModalRef.result.then(() => {
      if (!this.assignment) {
        return;
      }

      this.store.dispatch(
        baseAssignmentsActions.assignmentCustomerCancellation({
          payload: { assignment: this.assignment.id, body: this.deregisterForm.getRawValue() },
        })
      );
      this.isAssignmentDeRegistered = true;
    }, noop);
  }

  showSignContractsModal(event: Event) {
    event.stopPropagation();

    this.openSignContractsModal();
  }

  openSignContractsModal() {
    const canBeSigned = this.externalEmployeeService.canBeSigned(this.hasSignContractPermission, this.validContract);

    if (!canBeSigned) {
      return;
    }

    this.store.dispatch(baseAssignmentsActions.clearContractStatus());
    this.addSignModalRef = this.baseSignatureService.openSignContractModal(this.addSignSelectionPopup);
    this.addSignModalRef.result.then(
      () => {
        if (this.signingType === BaseSignContractOptions.signDigital) {
          this.openDigitalSignModal();
        } else {
          if (!this.assignment) {
            return;
          }

          this.baseSignatureService.dispatchAssignContractAction(
            this.assignment.id,
            this.validContract.id,
            this.signingType,
            true
          );
        }
      },
      () => {
        this.signingType = undefined;
      }
    );
  }

  onSignTypeChange(event: BaseSignContractOptions): void {
    this.signingType = event;
  }

  openInquireEmployeeModal() {
    this.store.dispatch(
      baseSharedActions.getCustomerDepartmentUsers({ payload: this.inquiry.customerDepartment?.id as number })
    );
    this.store.dispatch(
      baseSharedActions.loadCustomerDepartmentQualificationsList({
        payload: {
          department: this.inquiry.customerDepartment?.id as number,
          tag: this.environment.application.tag,
        },
      })
    );
    this.store.dispatch(baseInquiryActions.fetchDepartment({ payload: this.inquiry.customerDepartment?.id as number }));
    this.inquireEmployeeModalModalRef = this.modalService.open(
      {
        headText: 'externalEmployees.inquireSpecificEmployee',
        headTextTranslateParams: {
          employeeFullName: this.assignment
            ? `${this.assignment.profile.firstName} ${this.assignment.profile.lastName}`
            : '',
        },
        bodyTemplate: this.inquireEmployeeModal,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.inquireEmployeeModalModalRef.result.then(noop, noop);
  }

  private setTabs(): void {
    this.tabs =
      this.isOfferConfirmed && this.isAssignmentsAdmin
        ? [...BaseCustomerOfferInitialAdminTabList]
        : [...BaseCustomerOfferInitialTabList];

    if (!this.offer?.anonymous || this.isOfferConfirmed) {
      this.tabs.splice(2, 0, {
        id: BaseCustomerOfferTabsEnum.Reviews,
        title: 'ratings',
      });
    }

    if (!this.showWorkCouncilTab || !this.offer?.id) {
      this.tabs = this.tabs.filter((tab) => tab.id !== BaseCustomerOfferTabsEnum.WorkCouncil);
    }

    this.currentTab = this.tabs[BaseCustomerOfferTabsEnum.MaterData];
  }

  private setAssignmentEnded(): void {
    if (this.assignment) {
      this.isEndedAssignment = BaseDateService.isAfter(new Date(), new Date(this.assignment.endDate));
    }
  }

  private setAssignmentSignStatus(): void {
    this.isFullySigned = this.assignmentContractSigningStatus === BaseSigningStatus.signed;
    this.isSignedByPartner = this.assignmentContractSigningStatus === BaseSigningStatus.signed_by_psp;
    this.isSignedByMyself = this.assignmentContractSigningStatus === BaseSigningStatus.signed_by_customer;
    this.isNotSigned = this.assignmentContractSigningStatus === BaseSigningStatus.unsigned;
  }

  private setSignButtonClass(): string {
    return this.isFullySigned
      ? 'state-btn-border-green no-pointer'
      : this.isSignedByMyself
      ? 'state-btn-border-yellow no-pointer'
      : this.isNotSigned || this.isSignedByPartner
      ? 'state-btn-border-red no-pointer'
      : '';
  }

  private openDigitalSignModal() {
    this.digitalSignModalRef = this.externalEmployeeService.openDigitalSignModal(this.addSignPopup);
    this.digitalSignModalRef.result.then(() => noop, noop);
  }
}
